import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { BreadCrumb } from "primereact/breadcrumb";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { handleGetRequest } from "../../services/GetTemplate";
import { useDispatch } from "react-redux";
import { handlePostRequest } from "../../services/PostTemplate";
import { toast } from "react-toastify";
import axios from "axios";
import { DEV } from "../../services/constants";
import useViewOnly from "../../hooks/useViewOnly";
import styles from "./airportHandover.module.scss";

const STATUS_CLASS = {
    Scanning: "statusScanning",
    Generated: "statusGenerated",
    Completed: "statusCompleted",
};

const AirportHandovers = () => {
    const [handovers, setHandovers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModeDialog, setShowModeDialog] = useState(false);
    const history = useHistory();
    const dispatch = useDispatch();
    const isAdmin = localStorage.getItem("role") === "admin";
    const viewOnly = useViewOnly();

    const breadItems = [{ label: "Home", url: "/" }, { label: "Airport Handovers" }];
    const home = { icon: "pi pi-home", url: "/" };

    const loadHandovers = async () => {
        setLoading(true);
        const res = await handleGetRequest("/airport-handover");
        setHandovers(res?.data || []);
        setLoading(false);
    };

    useEffect(() => {
        loadHandovers();
    }, []);

    const createHandover = async (mode) => {
        const res = await dispatch(handlePostRequest({ mode }, "/airport-handover", true, true));
        if (res?.success) {
            setShowModeDialog(false);
            history.push(`/airport-handover/${res.data._id}`);
        } else {
            toast.error("Failed to create handover");
        }
    };

    const handleDelete = (e, handover) => {
        e.stopPropagation();
        confirmDialog({
            message: `Delete handover ${handover.handoverId}? This can't be undone. Any packages/clients it already handed over stay as-is - only the handover record itself is removed.`,
            header: "Delete Handover",
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",
            accept: async () => {
                try {
                    const response = await axios.delete(`${DEV}/airport-handover/${handover._id}`, {
                        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                    });
                    if (response?.data?.success) {
                        toast.success("Handover deleted");
                        setHandovers((prev) => prev.filter((h) => h._id !== handover._id));
                    } else {
                        toast.error(response?.data?.message || "Failed to delete handover");
                    }
                } catch (error) {
                    console.error("Error deleting handover:", error);
                    toast.error(error?.response?.data?.message || "Failed to delete handover");
                }
            },
        });
    };

    const stats = useMemo(() => {
        return {
            total: handovers.length,
            scanning: handovers.filter((h) => h.status === "Scanning").length,
            generated: handovers.filter((h) => h.status === "Generated").length,
            completed: handovers.filter((h) => h.status === "Completed").length,
        };
    }, [handovers]);

    return (
        <div className={styles.page}>
            <ConfirmDialog />
            <Dialog
                visible={showModeDialog}
                onHide={() => setShowModeDialog(false)}
                header={
                    <div className={styles.modeDialogHeader}>
                        <div className={styles.modeDialogHeaderIcon}>
                            <i className="pi pi-send" />
                        </div>
                        <div>
                            <h3>New Airport Handover</h3>
                            <p>Choose how you'll capture this handover</p>
                        </div>
                    </div>
                }
                className={styles.modeDialog}
                style={{ width: "min(640px, 95vw)" }}
            >
                <div className={styles.modeChoiceDialog}>
                    <button type="button" className={`${styles.modeOptionRow} ${styles.modeOptionIndigo}`} onClick={() => createHandover("standard")}>
                        <div className={styles.modeOptionIcon}>
                            <i className="pi pi-wifi" />
                        </div>
                        <div className={styles.modeOptionBody}>
                            <div className={styles.modeOptionTitle}>Building</div>
                            <p className={styles.modeOptionDescription}>Connected, live workflow inside the facility</p>
                            <div className={styles.modeOptionTags}>
                                <span>
                                    <i className="pi pi-qrcode" />
                                    Live scan
                                </span>
                                <span>
                                    <i className="pi pi-users" />
                                    Grouped by client
                                </span>
                                <span>
                                    <i className="pi pi-pencil" />
                                    Signature + 2 photos
                                </span>
                            </div>
                        </div>
                        <i className={`pi pi-chevron-right ${styles.modeOptionArrow}`} />
                    </button>

                    <button type="button" className={`${styles.modeOptionRow} ${styles.modeOptionAmber}`} onClick={() => createHandover("lowConnectivity")}>
                        <div className={styles.modeOptionIcon}>
                            <i className="pi pi-camera" />
                        </div>
                        <div className={styles.modeOptionBody}>
                            <div className={styles.modeOptionTitle}>Outside Building</div>
                            <p className={styles.modeOptionDescription}>Offline-friendly, for low or no connectivity</p>
                            <div className={styles.modeOptionTags}>
                                <span>
                                    <i className="pi pi-image" />
                                    Photo labels only
                                </span>
                                <span>
                                    <i className="pi pi-cloud" />
                                    Resolved in background
                                </span>
                            </div>
                        </div>
                        <i className={`pi pi-chevron-right ${styles.modeOptionArrow}`} />
                    </button>
                </div>
            </Dialog>

            <div className={styles.pageHeader}>
                <div>
                    <div className={styles.headerTitleRow}>
                        <div className={styles.headerIcon}>
                            <i className="pi pi-send" />
                        </div>
                        <div>
                            <h2>Airport Handovers</h2>
                            <p className={styles.subtitle}>Scan, group, and hand off packages at the airport</p>
                        </div>
                    </div>
                    {/* <BreadCrumb model={breadItems} home={home} /> */}
                </div>
                <div className={styles.headerActions}>
                    <Button
                        label="New Handover"
                        icon="pi pi-plus"
                        className={styles.addButton}
                        disabled={viewOnly}
                        tooltip={viewOnly ? "Clock in to start a handover" : undefined}
                        onClick={() => setShowModeDialog(true)}
                    />
                </div>
            </div>

            {!loading && handovers.length > 0 && (
                <div className={styles.statsRow}>
                    <div className={styles.statCard}>
                        <div className={`${styles.statIcon} ${styles.iconTotal}`}>
                            <i className="pi pi-box" />
                        </div>
                        <div>
                            <div className={styles.statValue}>{stats.total}</div>
                            <div className={styles.statLabel}>Total</div>
                        </div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={`${styles.statIcon} ${styles.iconScanning}`}>
                            <i className="pi pi-qrcode" />
                        </div>
                        <div>
                            <div className={styles.statValue}>{stats.scanning}</div>
                            <div className={styles.statLabel}>Scanning</div>
                        </div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={`${styles.statIcon} ${styles.iconGenerated}`}>
                            <i className="pi pi-sitemap" />
                        </div>
                        <div>
                            <div className={styles.statValue}>{stats.generated}</div>
                            <div className={styles.statLabel}>Generated</div>
                        </div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={`${styles.statIcon} ${styles.iconCompleted}`}>
                            <i className="pi pi-check-circle" />
                        </div>
                        <div>
                            <div className={styles.statValue}>{stats.completed}</div>
                            <div className={styles.statLabel}>Completed</div>
                        </div>
                    </div>
                </div>
            )}

            {loading ? (
                <div className={styles.loadingState}>
                    <ProgressSpinner strokeWidth="4" />
                    <span>Loading handovers...</span>
                </div>
            ) : handovers.length === 0 ? (
                <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>
                        <i className="pi pi-send" />
                    </div>
                    <h4>No airport handovers yet</h4>
                    <p>Start a new handover to scan packages, group them by client, and capture proof of delivery.</p>
                    <Button
                        label="New Handover"
                        icon="pi pi-plus"
                        className={styles.addButton}
                        disabled={viewOnly}
                        tooltip={viewOnly ? "Clock in to start a handover" : undefined}
                        onClick={() => setShowModeDialog(true)}
                    />
                </div>
            ) : (
                <div className={styles.grid}>
                    {handovers.map((h) => (
                        <div key={h._id} className={styles.card} onClick={() => history.push(`/airport-handover/${h._id}`)}>
                            <div className={styles.cardTop}>
                                <div className={styles.cardIdRow}>
                                    <div className={styles.modeIconSm}>
                                        <i className={h.mode === "standard" ? "pi pi-wifi" : "pi pi-camera"} />
                                    </div>
                                    <strong>{h.handoverId}</strong>
                                </div>
                                <div className={styles.cardTopActions}>
                                    <span className={`${styles.badge} ${styles[STATUS_CLASS[h.status]]}`}>{h.status}</span>
                                    {isAdmin && <Button icon="pi pi-trash" className="p-button-text p-button-danger p-button-sm" onClick={(e) => handleDelete(e, h)} tooltip="Delete handover" />}
                                </div>
                            </div>
                            <div className={styles.cardMeta}>
                                <div className={styles.metaRow}>
                                    <i className={h.mode === "standard" ? "pi pi-wifi" : "pi pi-camera"} />
                                    <span>{h.mode === "standard" ? "Building" : "Outside Building"}</span>
                                </div>
                                <div className={styles.metaRow}>
                                    <i className="pi pi-user" />
                                    <span>
                                        {h.createdBy?.firstName} {h.createdBy?.lastName}
                                    </span>
                                </div>
                                <div className={styles.metaRow}>
                                    <i className="pi pi-clock" />
                                    <span>{new Date(h.createdAt).toLocaleString()}</span>
                                </div>
                            </div>

                            <div className={styles.cardStats}>
                                <div className={styles.statChip}>
                                    <span className={styles.statChipValue}>{h.packageCount || 0}</span>
                                    <span className={styles.statChipLabel}>Packages</span>
                                </div>
                                <div className={styles.statChip}>
                                    <span className={styles.statChipValue}>{h.groupCount || 0}</span>
                                    <span className={styles.statChipLabel}>Groups</span>
                                </div>
                                <div className={styles.statChip}>
                                    <span className={styles.statChipValue}>{h.handedOverCount || 0}</span>
                                    <span className={styles.statChipLabel}>Handed Over</span>
                                </div>
                            </div>

                            {h.groupCount > 0 && (
                                <div className={styles.handedOverProgress}>
                                    <div className={styles.handedOverProgressBar}>
                                        <div className={styles.handedOverProgressFill} style={{ width: `${Math.round((h.handedOverCount / h.groupCount) * 100)}%` }} />
                                    </div>
                                    <p className={styles.handedOverProgressText}>
                                        {h.handedOverCount} of {h.groupCount} groups handed over
                                    </p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AirportHandovers;
